/**
 * Layout component that queries for data
 * with Gatsby's useStaticQuery component
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React, { useLayoutEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Link } from 'gatsby';
import { Helmet } from 'react-helmet';

import { MDXProvider } from '@mdx-js/react';
import { MDXRenderer } from 'gatsby-plugin-mdx';
import styled, { ThemeContext } from 'styled-components';
import { Box, Card, Grid, HorizontalStack, Select } from '@houzz/ui';
import AppLink from './app-link';
import Icon from './icon';
import CodeBlock from './codeblock';
import CodeSample from './code-sample';
import Heading from './heading';
import InlineCode from './inline-code';
import Summary from './summary';
import Github from './github';
import Hero from './hero';
import ThemeComponentToggle from './theme-component-toggle';
import ThemeValues from './theme-values';
import PropTable, { StyledTableHead, StyledTableCell } from './prop-table';
import StaticImage from './static-image';
import PrivateRoute from './private-route';
import HouzzAvatar from './houzz-avatar';
import { isLoggedIn } from '../services/auth';

import Navigation from './navigation';
import Search from './search';
import { NavigatonList } from './left-nav-menu';
import './layout.css';
import './left-nav.css';

const PATHS = {
    COMPONENTS: 'components',
    FOUNDATIONS: 'foundations',
    WRITING: 'writing',
    BRAND: 'brand',
    MARKETING: 'marketing',
};

const StyledTable = styled.table`
    width: auto;
    table-layout: fixed;
`;

const components = {
    a: AppLink,
    inlineCode: InlineCode,
    pre: CodeSample,
    code: CodeBlock,
    summary: Summary,
    h1: Heading('h1'),
    h2: Heading('h2'),
    h3: Heading('h3'),
    h4: Heading('h4'),
    h5: Heading('h5'),
    table: StyledTable,
    th: StyledTableHead,
    td: StyledTableCell,
    Card,
    Grid,
    Github,
    Hero,
    PropTable,
    StaticImage,
    ThemeComponentToggle,
    ThemeValues,
};

const getLinksOnPath = (nodes) => {
    const currentPath = window.location.pathname;
    const [rootPath] = currentPath
        .replace(/^\/houzz-ui/, '')
        .replace(/^\//, '')
        .split('/');

    if (!PATHS[rootPath.toUpperCase()]) {
        return [];
    }

    const pathRegExp = new RegExp(`^\\/${rootPath}\\/`, 'i');
    const links = nodes
        .filter((link) => {
            const {
                node: { frontmatter },
            } = link;
            if (!frontmatter) {
                return false;
            }

            const { path } = frontmatter;
            return pathRegExp.test(path);
        })
        .map((link) => {
            const {
                node: { frontmatter },
            } = link;
            return { ...frontmatter };
        });

    return [links, rootPath];
};

/**
 * Takes a flat list of frontmatter data and creates a tree for nested navigation
 */
const createMenuTree = (items = [], root = 'root') => {
    const result = [];
    const lookup = {};

    // Create lookup table for all parents and add a children list
    const allNodes = [{ title: root, path: root }, ...items].map((item) => {
        const path = item.path;
        const parts = path.replace(/^\//, '').split('/');
        const id = parts[parts.length - 1];
        lookup[id] = { ...item, children: [] };
        return lookup[id];
    });

    allNodes.forEach((item) => {
        const path = item.path;
        const parts = path.replace(/^\//, '').split('/');
        const parentId = parts[parts.length - 2];

        if (parentId && lookup[parentId]) {
            lookup[parentId].children.push(item);
        } else {
            result.push(item);
        }
    });

    return result;
};

const Layout = ({ data }) => {
    const { mdx, allMdx } = data;
    const { title, group, blurb, externalLink, status, isPrivate } = mdx.frontmatter;
    const { edges } = allMdx;

    const [, updateState] = useState({});
    const themeContext = useContext(ThemeContext);
    const { currentTheme, updateTheme, THEMES } = themeContext;
    const [currentLinks, setCurrentLinks] = useState([]);
    const [rootPath, setRootPath] = useState([]);

    const _isLoggedIn = isLoggedIn();

    const handleThemeToggle = (event) => updateTheme(event.target.value);
    const handleAuthRequest = () => {
        updateState({});
    };

    useLayoutEffect(() => {
        const [links, root] = getLinksOnPath(edges);
        setCurrentLinks(links);
        setRootPath(root);
    }, [edges]);

    const groupedLinks = currentLinks.reduce((acc, link) => {
        let { group } = link;
        if (!group) {
            group = 'default';
        }

        if (!acc[group]) {
            acc[group] = [];
        }
        acc[group].push(link);
        return acc;
    }, {});

    const sections = Object.keys(groupedLinks).sort();
    const nav = sections.map((section) => {
        const links = groupedLinks[section].sort((a, b) => (a.path < b.path ? -1 : 1));
        const menuTree = createMenuTree(links, rootPath);
        const navList = <NavigatonList key={section} links={menuTree} section={section.replace(/^!/, '')} />;
        return navList;
    });

    // query for all frontmatter data in docs folder and bucket them into one of the
    // above paths
    return (
        <div className="design-system-container--nav">
            <Helmet>
                <title>Houzz UI</title>
            </Helmet>
            <nav className="design-system__left-nav">
                <h1 style={{ margin: 0, position: 'relative' }}>
                    <Link to="/">
                        <div style={{ width: '114px' }}>
                            <Icon />
                        </div>
                    </Link>
                </h1>
                {nav}
            </nav>
            <section className="design-system__docs">
                <header>
                    <Navigation />
                    <HorizontalStack className="ext-links" alignItems="center">
                        <Search edges={edges} />
                        <Select
                            id="themeToggle"
                            onChange={handleThemeToggle}
                            onBlur={handleThemeToggle}
                            value={currentTheme}
                            labelText="Theme"
                        >
                            <option value="" disabled={true}>
                                Select theme
                            </option>
                            <option value={THEMES.CONSUMER}>Consumer</option>
                            <option value={THEMES.SAAS}>SaaS</option>
                            <option value={THEMES.SAAS_MONO}>SaaS Mono</option>
                        </Select>
                        <HouzzAvatar />
                    </HorizontalStack>
                </header>
                {isPrivate && !_isLoggedIn ? (
                    <PrivateRoute handleAuthRequest={handleAuthRequest} />
                ) : (
                    <MDXProvider components={components}>
                        <main>
                            {group && blurb && (
                                <Hero header={title} externalLink={externalLink} status={status}>
                                    {blurb}
                                </Hero>
                            )}
                            <article className={`main-content ${group && blurb ? 'main-content--narrow' : ''}`}>
                                <MDXRenderer
                                    pages={allMdx.edges}
                                    frontmatter={mdx.frontmatter}
                                    currentTheme={currentTheme}
                                >
                                    {mdx.body}
                                </MDXRenderer>
                            </article>
                        </main>
                    </MDXProvider>
                )}

                <footer>© {new Date().getFullYear()}, Houzz</footer>
            </section>
        </div>
    );
};

Layout.propTypes = {
    data: PropTypes.shape({
        mdx: PropTypes.object,
        allMdx: PropTypes.object,
    }),
};

export default Layout;

export const pageQuery = graphql`
    query BlogPostQuery($id: String) {
        allMdx {
            edges {
                node {
                    frontmatter {
                        path
                        title
                        group
                        status
                        blurb
                        isPrivate
                    }
                }
            }
        }
        mdx(id: { eq: $id }) {
            id
            body
            frontmatter {
                path
                title
                group
                status
                blurb
                externalLink
                isPrivate
            }
        }
    }
`;
